import { useCallback, useEffect, useMemo } from "react";
import { matchPath, useHistory, useLocation } from "react-router-dom";
import { FAY_ROUTE_BASE } from "../constants";
import { useActivityTabEnabled } from "./useActivityTabFlag";

export const PACKAGE_TABS = ["summary", "checks", "documents", "activity"] as const;

export type PackageTab = (typeof PACKAGE_TABS)[number];

/** Route pattern for the package segment under `base`. A host that mounts Fay
 *  deeper than `FAY_ROUTE_BASE` — Canopy serves it per arena, at
 *  `<base>/arena/:id` — needs the segment under its own prefix so the arena
 *  stays in the URL and a pasted deep link still names one.
 *
 *  Register the result as a route in the host. `base` may itself carry route
 *  params, so a host whose arena comes from the path registers
 *  `packageRoutePattern("<base>/arena/:id")` rather than interpolating a
 *  resolved arena id. */
export function packageRoutePattern(base: string = FAY_ROUTE_BASE): string {
  return `${base}/package/:packageId/:tab?`;
}

export const PACKAGE_ROUTE_PATTERN = packageRoutePattern();

const DEFAULT_TAB: PackageTab = "summary";

export function isPackageTab(value: unknown): value is PackageTab {
  return typeof value === "string" && (PACKAGE_TABS as readonly string[]).includes(value);
}

export function packagePath(
  packageId: string,
  tab: PackageTab,
  base: string = FAY_ROUTE_BASE,
): string {
  return `${base}/package/${packageId}/${tab}`;
}

/** `history` runs `decodeURI` on every pathname it is handed and throws a
 *  `URIError` on a stray percent (an id of `pkg%` becomes `pkg%/summary` once a
 *  tab is appended). Every write below checks the path first — a throw out of
 *  the grid's click handler would escape the page's ErrorBoundary, which only
 *  catches render errors. Nothing is lost by declining to navigate: such an id
 *  is malformed, so the package would resolve to not-found anyway. */
function isNavigablePath(path: string): boolean {
  try {
    decodeURI(path);
    return true;
  } catch {
    return false;
  }
}

/** Marks history entries this hook pushed, so closing can pop the entry back
 *  off instead of stacking a duplicate the reviewer has to Back through
 *  twice. Absent on a pasted deep link, where there is nothing to pop. */
type PackageRouteState = { fayPackageOpened?: boolean };

export type PackageRoute = {
  packageId: string | null;
  tab: PackageTab;
  openPackage: (packageId: string, tab?: PackageTab) => void;
  setTab: (tab: PackageTab) => void;
  closePackage: () => void;
};

/** `routeBase` is where the host app serves Fay. It defaults to
 *  `FAY_ROUTE_BASE`, which is where `main` serves it; Canopy passes its
 *  arena-scoped prefix so every path this hook reads and writes keeps the arena
 *  the surface is scoped to.
 *
 *  A host passing a non-default base must also register a route matching
 *  `packageRoutePattern(routeBase)`. Without it, opening a package appears to
 *  work — this hook writes the path and the slideout opens — but a reload or a
 *  pasted link lands on a path the host does not serve, so the surface
 *  redirects or 404s instead of reopening the package. */
export function usePackageRoute(routeBase: string = FAY_ROUTE_BASE): PackageRoute {
  const history = useHistory();
  const location = useLocation();
  const activityTabEnabled = useActivityTabEnabled();

  // Parsed with `matchPath` rather than a nested `<Switch>` so `CoverageView`
  // stays mounted as the package segment comes and goes — a remount would
  // refetch the coverage grid and drop the URL-seeded filters.
  const match = useMemo(
    () =>
      matchPath<{ packageId: string; tab?: string }>(location.pathname, {
        path: packageRoutePattern(routeBase),
        exact: true,
      }),
    [location.pathname, routeBase],
  );

  const packageId = match?.params.packageId || null;
  const rawTab = match?.params.tab;
  const parsedTab: PackageTab = isPackageTab(rawTab) ? rawTab : DEFAULT_TAB;
  // Gating resolution rather than the predicate keeps `isPackageTab` a pure
  // check over the whole tuple, and means a deep link to `/activity` with the
  // flag off canonicalizes away to the default tab in the effect below rather
  // than parking the reviewer on a tab nothing renders.
  const activityTabHidden = parsedTab === "activity" && !activityTabEnabled;
  const tab: PackageTab = activityTabHidden ? DEFAULT_TAB : parsedTab;

  // Opening a package is a real navigation (back returns to the grid); tab
  // switches and canonicalizing are `replace` so the back button never walks a
  // reviewer through their own tab clicks. Every write spreads the current
  // location so the coverage filters in `search` survive.
  useEffect(() => {
    if (!packageId) {
      return;
    }
    const canonicalPath = packagePath(packageId, tab, routeBase);
    if (location.pathname !== canonicalPath && isNavigablePath(canonicalPath)) {
      history.replace({ ...history.location, pathname: canonicalPath });
    }
  }, [history, location.pathname, packageId, routeBase, tab]);

  const openPackage = useCallback(
    (nextPackageId: string, nextTab: PackageTab = DEFAULT_TAB) => {
      const nextPath = packagePath(nextPackageId, nextTab, routeBase);
      if (!isNavigablePath(nextPath)) {
        return;
      }
      const state = (history.location.state ?? {}) as Record<string, unknown>;
      history.push({
        ...history.location,
        pathname: nextPath,
        state: { ...state, fayPackageOpened: true },
      });
    },
    [history, routeBase],
  );

  const setTab = useCallback(
    (nextTab: PackageTab) => {
      if (!packageId) {
        return;
      }
      const nextPath = packagePath(packageId, nextTab, routeBase);
      if (!isNavigablePath(nextPath)) {
        return;
      }
      history.replace({ ...history.location, pathname: nextPath });
    },
    [history, packageId, routeBase],
  );

  // Popping our own pushed entry keeps the back button honest: `replace` would
  // leave a duplicate of the grid behind the current one, so the first Back
  // press after an open/close round trip would look like it did nothing. A
  // pasted deep link has no entry of ours to pop, so it rewrites instead.
  const closePackage = useCallback(() => {
    const state = history.location.state as PackageRouteState | undefined;
    if (state?.fayPackageOpened) {
      history.goBack();
      return;
    }
    history.replace({ ...history.location, pathname: routeBase });
  }, [history, routeBase]);

  return { packageId, tab, openPackage, setTab, closePackage };
}
